import React from 'react'
import styled, { css, keyframes } from 'styled-components'

export interface LoadingProps
    extends LoadingStyleProps,
        React.HTMLAttributes<HTMLSpanElement> {
    label?: string
}

export const LoadingIndicator: React.FC<LoadingProps> = ({
    size = 'medium',
    variant = 'primary',
    label,
    className,
    ...props
}) => {
    const ariaLabel = label ?? 'Loading'

    const { popover, ...safeProps } = props

    return (
        <SpinnerContainer
            role="status"
            aria-live="polite"
            aria-label={ariaLabel}
            className={className}
            {...safeProps}
        >
            <SpinnerSvg
                viewBox="0 0 50 50"
                size={size}
                variant={variant}
                focusable={false}
            >
                <SpinnerCircle cx="25" cy="25" r="20" />
            </SpinnerSvg>
            {label && <LabelText size={size}>{label}</LabelText>}
        </SpinnerContainer>
    )
}

export interface LoadingStyleProps {
    size?: 'tiny' | 'small' | 'medium' | 'large'
    variant?: 'primary' | 'secondary' | 'neutral'
}

const rotate = keyframes`
    100% {
        transform: rotate(360deg);
    }
`

const dash = keyframes`
    0% {
        stroke-dasharray: 1, 150;
        stroke-dashoffset: 0;
    }
    50% {
        stroke-dasharray: 90, 150;
        stroke-dashoffset: -35;
    }
    100% {
        stroke-dasharray: 90, 150;
        stroke-dashoffset: -124;
    }
`

const getSizeStyles = (size: LoadingStyleProps['size'] = 'medium') => {
    switch (size) {
        case 'tiny':
            return css`
                width: ${({ theme }) => theme.spacing[4]};
                height: ${({ theme }) => theme.spacing[4]};
            `
        case 'small':
            return css`
                width: ${({ theme }) => theme.spacing[6]};
                height: ${({ theme }) => theme.spacing[6]};
            `
        case 'medium':
            return css`
                width: ${({ theme }) => theme.spacing[8]};
                height: ${({ theme }) => theme.spacing[8]};
            `
        case 'large':
            return css`
                width: ${({ theme }) => theme.spacing[10]};
                height: ${({ theme }) => theme.spacing[10]};
            `
        default:
            return css``
    }
}

const getVariantColor = (variant: LoadingStyleProps['variant'] = 'primary') => {
    switch (variant) {
        case 'primary':
            return css`
                color: ${({ theme }) => theme.colors.info};
            `
        default:
            return css``
    }
}

export const SpinnerContainer = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
    flex-direction: column;
    line-height: 0;
    gap: ${({ theme }) => theme.spacing[5]};
`

export const SpinnerSvg = styled.svg.withConfig({
    shouldForwardProp: (prop) => !['size', 'variant'].includes(prop),
})<LoadingStyleProps>`
    display: block;
    transform-origin: center;
    animation: ${rotate} 1s linear infinite;
    ${({ size }) => getSizeStyles(size)}
    ${({ variant }) => getVariantColor(variant)}
`

export const SpinnerCircle = styled.circle`
    fill: none;
    stroke: currentColor;
    stroke-width: 4;
    stroke-linecap: round;
    animation: ${dash} 1.5s ease-in-out infinite;
`

const LabelText = styled.span<{ size: LoadingStyleProps['size'] }>`
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ theme }) => theme.colors.text.primary};

    ${({ size, theme }) => css`
        ${size === 'tiny' &&
        `
            font-size: ${theme.typography.fontSize.xs};
        `}
        ${size === 'small' &&
        `
            font-size: ${theme.typography.fontSize.sm};

        `}
        ${size === 'medium' &&
        `
        font-size: ${theme.typography.fontSize.base};
        `}

        ${size === 'large' &&
        `
        `}
            font-size: ${theme.typography.fontSize.lg};
    `}
`
